import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="wizard-progress"
export default class extends Controller {
  static targets = ["progressFill", "movingCircle"];
  static values = {
    currentStep: Number,
    totalSteps: Number,
  };

  connect() {
    this.updateProgress();

    // Listen for custom update events as fallback
    this.element.addEventListener(
      "wizard-progress:update",
      this.handleCustomUpdate.bind(this)
    );

    // Listen for turbo navigation events
    document.addEventListener("turbo:load", this.handleTurboLoad.bind(this));
    document.addEventListener(
      "turbo:frame-load",
      this.handleTurboFrameLoad.bind(this)
    );
  }

  disconnect() {
    this.element.removeEventListener(
      "wizard-progress:update",
      this.handleCustomUpdate.bind(this)
    );
    document.removeEventListener("turbo:load", this.handleTurboLoad.bind(this));
    document.removeEventListener(
      "turbo:frame-load",
      this.handleTurboFrameLoad.bind(this)
    );
  }

  handleCustomUpdate(event) {
    const stepNumber = event.detail.stepNumber;
    this.updateStep(stepNumber);
  }

  handleTurboLoad() {
    // Update progress on full page navigation
    this.updateProgressFromWizardFrame();
  }

  handleTurboFrameLoad(event) {
    // Update progress on frame navigation
    if (event.target.id === "wizard_content") {
      this.updateProgressFromWizardFrame();
    }
  }

  updateProgressFromWizardFrame() {
    const wizardFrame = document.getElementById("wizard_content");
    if (wizardFrame) {
      const stepNumber = wizardFrame.dataset.wizardStep;
      if (stepNumber) {
        this.updateStep(parseInt(stepNumber));
      }
    }
  }

  currentStepValueChanged() {
    this.updateProgress();
  }

  updateProgress() {
    const progressPercentage =
      (this.currentStepValue / this.totalStepsValue) * 100;

    // Update progress bar fill with smooth animation
    const progressFill = this.progressFillTarget;
    if (progressFill) {
      progressFill.style.width = `${progressPercentage}%`;
    }

    // Update moving circle position
    const movingCircle = this.movingCircleTarget;
    if (movingCircle) {
      movingCircle.style.left = `${progressPercentage}%`;

      // Update circle content based on step
      const checkmark = movingCircle.querySelector("svg");

      if (this.currentStepValue > 1) {
        // Show checkmark for completed steps
        if (checkmark) checkmark.style.display = "block";
      } else {
        // Hide checkmark for first step
        if (checkmark) checkmark.style.display = "none";
      }
    }

    // Update step labels
    const labels = document.querySelectorAll(".wizard-step-label");
    labels.forEach((label, index) => {
      const stepNumber = index + 1;

      // Reset classes
      label.classList.remove("text-brand-400", "font-medium", "text-gray-400");

      // Apply appropriate styles based on step state
      if (stepNumber <= this.currentStepValue) {
        // Active/completed step
        label.classList.add("text-brand-400", "font-medium");
      } else {
        // Future step
        label.classList.add("text-gray-400");
      }
    });
  }

  // Method to be called when navigating to a new step
  updateStep(newStep) {
    this.currentStepValue = newStep;
  }
}

// Global function to update wizard progress from anywhere
window.updateWizardProgress = function (stepNumber) {
  const progressElement = document.querySelector(
    '[data-controller*="wizard-progress"]'
  );

  if (progressElement) {
    // Try multiple ways to get the Stimulus controller
    let controller = null;

    // Method 1: Try to get from the element's Stimulus application
    if (progressElement.application) {
      controller =
        progressElement.application.getControllerForElementAndIdentifier(
          progressElement,
          "wizard-progress"
        );
    }

    // Method 2: Try to get from global Stimulus
    if (!controller && window.Stimulus) {
      controller = window.Stimulus.getControllerForElementAndIdentifier(
        progressElement,
        "wizard-progress"
      );
    }

    // Method 3: Try to access the controller directly from the element
    if (!controller && progressElement.stimulusControllers) {
      controller = progressElement.stimulusControllers.find(
        (c) => c.identifier === "wizard-progress"
      );
    }

    // Method 4: Fallback - directly update the data attributes and trigger change
    if (!controller) {
      progressElement.setAttribute(
        "data-wizard-progress-current-step-value",
        stepNumber
      );
      // Trigger a custom event to force update
      progressElement.dispatchEvent(
        new CustomEvent("wizard-progress:update", {
          detail: { stepNumber },
        })
      );
    } else {
      controller.updateStep(stepNumber);
    }
  }
};
